/**
 * 公告栏整页板面（03 开发文档 + 定稿原型 notices.html + notice-board.js 逐行移植）。
 * 结构/类名/交互原样保留；接线替换：?lang= → useLocale()，ERAS 硬编码 → GET /public/notices。
 * 组件映射：NoticeBoard（纸张渲染）+ EraTimeRail（滑杆/步进/刻度 + aria-live）+ PaperReader（阅读层）。
 */
import { useEffect, useRef, useState } from 'react';
import type { LocaleMeta, NoticeEra, NoticeTone } from '@chronospeak/contracts';
import type { Envelope } from '../lib/api-types';
import { http } from '../lib/http';
import { useLocale } from '../lib/locale';
import { copy } from '../lib/i18n';
import { DraftBadge, LocaleFallbackNotice } from '../components/bilingual';
import { SiteHeader } from '../components/SiteHeader';

/** era 投影（API 返回；papers 挂在 era 上） */
interface EraWithPapers extends NoticeEra {
  papers: {
    slug: string;
    title: string;
    publishedAt: string;
    tone: NoticeTone;
    excerpt: string;
    eraKey: string;
  }[];
}

/** 纸页阅读层数据（正文打开时拉取） */
interface PaperDetail {
  slug: string;
  title: string;
  publishedAt: string;
  tone: NoticeTone;
  html: string;
}

/** ISO 日期 → 板面纸面日期（原型格式 2026.08.18） */
function paperDate(iso: string): string {
  return iso.slice(0, 10).replaceAll('-', '.');
}

export function NoticesPage() {
  const { locale } = useLocale();
  const [envelope, setEnvelope] = useState<Envelope<{ eras: EraWithPapers[] }> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setEnvelope(null);
    setError(null);
    http
      .get<Envelope<{ eras: EraWithPapers[] }>>(`/public/notices?locale=${locale}`)
      .then((res) => {
        if (!cancelled) setEnvelope(res);
      })
      .catch(() => {
        if (!cancelled) setError(copy('common.loadFailed', locale));
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // 时期导航状态：index 初始 = 最近期（滑杆最右），aria-live 播报
  const [eraIndex, setEraIndex] = useState(0);
  const eras = envelope?.data.eras ?? [];
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && eras.length > 0) {
      setEraIndex(eras.length - 1);
      initialized.current = true;
    }
  }, [eras.length]);

  // 埋点：board_viewed（进入时一次）+ era_changed（切换时）
  useEffect(() => {
    if (!envelope) return;
    void import('../lib/locale').then(({ track }) =>
      track('notice_board_viewed', {
        locale,
        eraKey: eras[eraIndex]?.key ?? '',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelope]);

  const currentEra = eras[eraIndex];

  const changeEra = (index: number, method: 'slider' | 'step' | 'tick') => {
    if (index < 0 || index >= eras.length || index === eraIndex) return;
    setEraIndex(index);
    void import('../lib/locale').then(({ track }) =>
      track('notice_era_changed', { eraKey: eras[index]?.key ?? '', method, locale }),
    );
  };

  return (
    <main className='page notice-board-page'>
      <SiteHeader />
      <section className='board-scene' data-board-scene aria-label={locale === 'zh-CN' ? '公告栏板面' : 'Noticeboard'}>
        <h1 className='board-title'>{locale === 'zh-CN' ? '公告栏' : 'Noticeboard'}</h1>
        {error && (
          <p role='alert' style={{ color: '#e8a9a0', padding: '0 24px' }}>
            <span aria-hidden='true'>✎ </span>
            {error}
          </p>
        )}
        {!envelope && !error && <p style={{ color: 'var(--muted)', padding: '0 24px' }}>{copy('common.loading', locale)}</p>}
        {envelope && <LocaleFallbackNotice fallbackUsed={(envelope as LocaleMeta).fallbackUsed} />}
        {envelope && (
          <>
            <BoardPapers era={currentEra} locale={locale} />
            <div className='board-time'>
              <button
                type='button'
                className='time-step'
                data-time-prev
                aria-label={locale === 'zh-CN' ? '上一个时期' : 'Previous era'}
                disabled={eraIndex <= 0}
                onClick={() => changeEra(eraIndex - 1, 'step')}
              />
              <div className='time-rail'>
                <input
                  className='time-slider'
                  data-time-slider
                  type='range'
                  min={0}
                  max={Math.max(0, eras.length - 1)}
                  step={1}
                  value={eraIndex}
                  aria-label={locale === 'zh-CN' ? '拨动公告栏的时间' : 'Move through the noticeboard timeline'}
                  onChange={(e) => changeEra(Number(e.target.value), 'slider')}
                />
                <div className='time-ticks' data-time-ticks>
                  {eras.map((era, i) => (
                    <button
                      key={era.key}
                      type='button'
                      className={`time-tick${i === eraIndex ? ' is-active' : ''}`}
                      aria-label={`${era.name} ${era.dateRange}`}
                      onClick={() => changeEra(i, 'tick')}
                    >
                      {/* 刻度只按时段标注（原型 tickLabel），完整时期信息在板面下方 */}
                      {era.tickLabel}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type='button'
                className='time-step'
                data-time-next
                aria-label={locale === 'zh-CN' ? '下一个时期' : 'Next era'}
                disabled={eraIndex >= eras.length - 1}
                onClick={() => changeEra(eraIndex + 1, 'step')}
              />
            </div>
            <p className='board-era' data-board-era aria-live='polite'>
              {currentEra && (
                <>
                  <b>{currentEra.name}</b>
                  <time>{currentEra.dateRange}</time>
                </>
              )}
            </p>
          </>
        )}
        <noscript>
          <nav className='board-noscript'>
            <a href={`/${locale}/notices`}>{locale === 'zh-CN' ? '小镇开镇记' : 'The founding'}</a>
          </nav>
        </noscript>
      </section>
    </main>
  );
}

/** 当前时期纸张排布（原型 render() 的 papers.map 段） */
function BoardPapers({ era, locale }: { era: EraWithPapers | undefined; locale: 'zh-CN' | 'en' }) {
  const [reading, setReading] = useState<PaperDetail | null>(null);
  const lastTrigger = useRef<HTMLElement | null>(null);

  // 阅读层：Esc 关闭（原型 document keydown）——走 closePaper 保焦点回归
  const closePaperRef = useRef(() => {});
  useEffect(() => {
    if (!reading) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePaperRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [reading]);

  // 锁滚动
  useEffect(() => {
    document.body.style.overflow = reading ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [reading]);

  /** 纸页阅读层打开后焦点进关闭钮（原型 openPaper reader.querySelector(close).focus()）；关闭钮挂 ref 供聚焦 */
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (reading) closeBtnRef.current?.focus();
  }, [reading]);

  const openPaper = async (slug: string) => {
    lastTrigger.current = document.activeElement as HTMLElement;
    void import('../lib/locale').then(({ track }) => track('notice_opened', { noticeSlug: slug, source: 'board', locale }));
    try {
      const detail = await http.get<Envelope<PaperDetail>>(`/public/notices/${encodeURIComponent(slug)}?locale=${locale}`);
      setReading(detail.data);
    } catch {
      // 公开 404（下线即消失）——阅读层不开
    }
  };

  const closePaper = () => {
    setReading(null);
    // 焦点回归触发纸张（原型 closePaper）
    lastTrigger.current?.focus();
  };
  closePaperRef.current = closePaper;

  if (!era) return null;

  return (
    <>
      <div className='board' data-board data-mood={era.mood}>
        {era.papers.length === 0 ? (
          <p className='board-empty' data-board-empty>
            {locale === 'zh-CN' ? '这个时期板子上还没有纸。' : 'Nothing was pinned on the board in this period.'}
          </p>
        ) : (
          era.papers.map((p, i) => (
            <button
              key={p.slug}
              type='button'
              className={`paper tone-${p.tone}`}
              data-paper={p.slug}
              style={{ ['--stagger' as string]: String(i) }}
              aria-label={`${p.title.replace('[机翻未审] ', '')} - ${paperDate(p.publishedAt)}`}
              onClick={() => void openPaper(p.slug)}
            >
              <time className='paper-date'>{paperDate(p.publishedAt)}</time>
              <h2>
                {p.title}
                <DraftBadge text={p.title} />
              </h2>
              <p>{p.excerpt}</p>
              <span className='paper-open'>
                {copy('common.readMore', locale)} <span aria-hidden='true'>→</span>
              </span>
            </button>
          ))
        )}
      </div>

      {/* 纸页全文阅读层（模态；URL 不变——03 开发文档） */}
      <div
        className='paper-reader'
        role='dialog'
        aria-modal='true'
        aria-label={locale === 'zh-CN' ? '公告全文' : 'Notice full text'}
        hidden={!reading}
        onClick={(e) => {
          if (e.target === e.currentTarget) closePaper();
        }}
      >
        {reading && (
          <article className='paper-sheet is-open' data-paper-sheet>
            <button
              type='button'
              className='paper-close'
              aria-label={locale === 'zh-CN' ? '关闭' : 'Close'}
              ref={closeBtnRef}
              onClick={closePaper}
            />
            <p className='paper-date'>{paperDate(reading.publishedAt)}</p>
            <h2>
              {reading.title}
              <DraftBadge text={reading.title} />
            </h2>
            {/* 服务端已净化（sanitize-html 白名单 + noopener）——React dangerHTML 注入安全 */}
            <div dangerouslySetInnerHTML={{ __html: reading.html }} />
            <p className='paper-sign'>
              — {locale === 'zh-CN' ? '小镇记' : 'From the Town'} <span aria-hidden='true'>→</span>
            </p>
          </article>
        )}
      </div>
    </>
  );
}
